import { useEffect, useRef, useState, useCallback } from 'react';
import ChatMessage from './ChatMessage';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'separator' | 'notification';
  content: string;
  createdAt: string;
}

interface ChatHistoryProps {
  messages: Message[];
  loading: boolean;
  streamingId?: string;
  agentType?: string;
  /** Callback for interactive option widgets in assistant messages */
  onSendMessage?: (content: string) => void;
  /** Whether the session is read-only (historical view) */
  readOnly?: boolean;
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2 px-4 py-3" data-testid="typing-indicator">
      <BluePrintWaitingLogo />
      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>BluePrint is thinking…</span>
    </div>
  );
}

/**
 * Inline animation of the BluePrint mark used in the chat wait indicator.
 * Each of the four stacked layers fades in sequence, and the pink accent
 * dot at the apex breathes on a slower loop. Inlined (not an <img>) so
 * the keyframes can target individual elements without an iframe
 * boundary, and so the asset never 404s when the page is offline.
 */
function BluePrintWaitingLogo() {
  return (
    <span
      data-testid="typing-indicator-logo"
      aria-hidden
      style={{ display: 'inline-flex', width: 22, height: 22, flexShrink: 0 }}
    >
      <style>{`
        @keyframes bp-layer-pulse {
          0%, 100% { opacity: var(--bp-layer-base); }
          50%      { opacity: 1; }
        }
        @keyframes bp-dot-breathe {
          0%, 100% { transform: scale(1);   opacity: 0.95; }
          50%      { transform: scale(1.3); opacity: 1;    }
        }
        .bp-wait-layer {
          animation: bp-layer-pulse 1.2s ease-in-out infinite;
        }
        .bp-wait-dot {
          transform-origin: 24px 9px;
          animation: bp-dot-breathe 1.2s ease-in-out infinite;
        }
      `}</style>
      <svg viewBox="0 0 48 48" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="bp-wait-bg" x1="4" y1="4" x2="44" y2="44">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#4C1D95" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="40" height="40" rx="10" fill="url(#bp-wait-bg)" />
        {/* Stacked layers — deepest first. CSS variable holds the
            resting opacity; the keyframe animates from that to 1 and
            back, so the deepest layer ripples gently while the topmost
            stays mostly bright. Staggered via animationDelay. */}
        <path d="M10 34 L24 27 L38 34 L24 41 Z" fill="white" className="bp-wait-layer" style={{ ['--bp-layer-base' as string]: 0.25, animationDelay: '0s' }} />
        <path d="M10 28 L24 21 L38 28 L24 35 Z" fill="white" className="bp-wait-layer" style={{ ['--bp-layer-base' as string]: 0.45, animationDelay: '0.15s' }} />
        <path d="M10 22 L24 15 L38 22 L24 29 Z" fill="white" className="bp-wait-layer" style={{ ['--bp-layer-base' as string]: 0.65, animationDelay: '0.3s' }} />
        <path d="M10 16 L24 9 L38 16 L24 23 Z" fill="white" className="bp-wait-layer" style={{ ['--bp-layer-base' as string]: 0.95, animationDelay: '0.45s' }} />
        <circle cx="24" cy="9" r="3" fill="#EC4899" className="bp-wait-dot" />
      </svg>
    </span>
  );
}

function SkeletonMessage({ isUser }: { isUser: boolean }) {
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} animate-pulse`}>
      {!isUser && <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200" />}
      <div className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`h-10 rounded-2xl bg-gray-200 ${isUser ? 'w-48' : 'w-64'}`} />
        <div className="h-3 w-12 rounded bg-gray-100" />
      </div>
    </div>
  );
}

/**
 * Pixels of slack at the bottom of the chat-scroll area before we treat the
 * user as "pinned to bottom". Anything within this distance of the bottom
 * still auto-follows new tokens; once the user scrolls farther up than this
 * we stop following so they can read history without being yanked back.
 */
const PIN_THRESHOLD_PX = 120;

export default function ChatHistory({ messages, loading, streamingId, agentType, onSendMessage, readOnly }: ChatHistoryProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastLengthRef = useRef(0);
  // Pinned-to-bottom state. True until the user scrolls more than
  // PIN_THRESHOLD_PX above the bottom; flips back when they return.
  const pinnedToBottomRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  // RAF-coalescing handle. Multiple streaming chunks arriving in the same
  // frame collapse to one scroll write — eliminates the layout-thrash that
  // produced visible jerkiness on large artefacts.
  const rafIdRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  // Resolve the actual scrolling ancestor on mount. The chat scroll area
  // sits above ChatHistory in ChatPage and carries `data-testid=
  // "chat-scroll-area"` — the same element bottomRef.scrollIntoView walks
  // up to. We read it directly so the user-scroll handler attaches once
  // and so streaming-mode scroll writes can target `scrollTop` directly
  // (cheaper than scrollIntoView and immune to the smooth-animation
  // interruption that caused the jerkiness).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const container = wrapper.closest<HTMLElement>('[data-testid="chat-scroll-area"]')
      ?? (wrapper.parentElement as HTMLElement | null);
    scrollContainerRef.current = container;
    if (!container) return;

    const recompute = () => {
      const distFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
      const pinned = distFromBottom <= PIN_THRESHOLD_PX;
      pinnedToBottomRef.current = pinned;
      // Hide the "jump to latest" CTA whenever we're pinned. When the
      // user has scrolled away during a stream, surface it.
      setShowJumpToLatest(!pinned);
    };
    recompute();
    container.addEventListener('scroll', recompute, { passive: true });
    return () => container.removeEventListener('scroll', recompute);
  }, []);

  /**
   * Schedule one instant scroll-to-bottom write per animation frame.
   * Coalesces N streaming-token updates per frame into one paint. No
   * animation, so consecutive calls cannot interrupt each other — the
   * source of the original jerk.
   *
   * Codex P1 on #688: the target MUST be `bottomRef`, not the scroll
   * container's `scrollHeight`. The chat-scroll-area also contains
   * artefact / conflict cards rendered AFTER ChatHistory in ChatPage;
   * setting `container.scrollTop = container.scrollHeight` scrolls past
   * the streaming tokens to the end of those cards. Anchoring on
   * `bottomRef` (which sits at the end of ChatHistory's message list,
   * before any post-chat cards) keeps the viewport on the live tail.
   */
  const scheduleInstantScrollToBottom = useCallback(() => {
    if (rafIdRef.current !== null) return; // already scheduled this frame
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'instant' as ScrollBehavior });
    });
  }, []);

  // Auto-scroll behaviour split by mode (post fix/chat-streaming-scroll-jank):
  //
  // - Initial mount with ONLY the welcome bubble → do NOT scroll. Anything
  //   above ChatHistory in the scroll container (Dev Agent IssuePicker,
  //   Selected-Issue card, agent identity banner) must stay visible so the
  //   user can interact with it. See fix/dev-agent-chat-scroll-layout: this
  //   was a P2 regression flagged by Codex on #682.
  // - Initial mount with real messages → jump to the bottom instantly.
  //   Smooth scrolling over a 500-message historical session queues a
  //   multi-second animation that competes with the synchronous render
  //   work of heavy children (Mermaid, syntax highlighting), which is the
  //   symptom users report as "archived session view is unresponsive".
  // - New message appended (length grew) → smooth-scroll once, only when
  //   the user is pinned to the bottom. A length growth is a discrete
  //   event so the smooth animation has room to complete before the next
  //   call, and we already gated that on `!readOnly`.
  // - Streaming update (length unchanged, content of an existing message
  //   grew) → instant, RAF-coalesced direct `scrollTop = scrollHeight`.
  //   This is the core jerkiness fix: 50–100 chunks/sec used to fire 50–100
  //   `scrollIntoView({ behavior: 'smooth' })` calls per second, each one
  //   cancelling the previous 300ms animation mid-flight. RAF + instant
  //   collapses that into one repaint per frame.
  // - User has scrolled away from the bottom → we DO NOT auto-scroll on
  //   either path. The "Jump to latest" button takes over so the reader
  //   stays in control.
  // - Read-only sessions after initial mount → still do NOT scroll. Same
  //   reasoning as before.
  useEffect(() => {
    const prev = lastLengthRef.current;
    const curr = messages.length;
    lastLengthRef.current = curr;

    if (curr === 0) return; // nothing to scroll to
    const onlyWelcome = curr === 1 && messages[0]?.id === '__welcome__';
    if (prev === 0) {
      if (onlyWelcome) return; // keep IssuePicker / banner above visible
      bottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      return;
    }
    if (readOnly) return;
    if (!pinnedToBottomRef.current) return; // user has scrolled away — leave them alone

    if (curr > prev) {
      // Discrete new-message append — keep the smooth animation users expect.
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Streaming token update on an existing message — RAF-coalesced
      // instant scroll so the viewport follows the tail without jerk.
      scheduleInstantScrollToBottom();
    }
  }, [messages, readOnly, scheduleInstantScrollToBottom]);

  const handleJumpToLatest = useCallback(() => {
    pinnedToBottomRef.current = true;
    setShowJumpToLatest(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div ref={wrapperRef} className="px-6 py-4 space-y-4" style={{ position: 'relative' }}>
      {loading ? (
        <>
          <SkeletonMessage isUser={false} />
          <SkeletonMessage isUser={true} />
          <SkeletonMessage isUser={false} />
        </>
      ) : (
        messages.map(msg => {
          // Skip rendering the empty streaming message — TypingIndicator replaces it below
          if (msg.id === streamingId && !msg.content) return null;

          return msg.role === 'separator' ? (
            <div key={msg.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                color: 'var(--text-secondary)', whiteSpace: 'nowrap',
              }}>
                {msg.content}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          ) : msg.role === 'notification' ? (
            <div key={msg.id} style={{
              display: 'flex', justifyContent: 'center', padding: '6px 0',
            }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', borderRadius: 'var(--radius-sm)',
                background: 'rgba(124,58,237,0.06)',
                border: '1px solid rgba(124,58,237,0.18)',
              }}>
                <span style={{ fontSize: 14 }}>✓</span>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  color: 'var(--primary)', lineHeight: 1.4,
                }}>
                  {msg.content}
                </span>
              </div>
            </div>
          ) : (
            <ChatMessage
              key={msg.id}
              role={msg.role}
              content={msg.content}
              createdAt={msg.createdAt}
              streaming={msg.id === streamingId}
              agentType={agentType}
              onSendMessage={onSendMessage}
              readOnly={readOnly}
            />
          );
        })
      )}
      {streamingId && (() => {
        const streamMsg = messages.find(m => m.id === streamingId);
        return streamMsg && !streamMsg.content ? <TypingIndicator /> : null;
      })()}
      <div ref={bottomRef} />

      {/*
        "Jump to latest" floating CTA. Visible only when the user has
        scrolled more than PIN_THRESHOLD_PX above the bottom — so it
        appears precisely when streaming would otherwise have stolen
        their reading position. Click snaps to the bottom (re-enabling
        auto-follow). The button itself is `position: sticky` inside the
        relatively-positioned wrapper so it floats above the message
        list without escaping the chat-scroll container.
      */}
      {showJumpToLatest && !readOnly && (
        <div
          style={{
            position: 'sticky', bottom: 12, marginLeft: 'auto', marginRight: 0,
            // The sticky element needs a parent boundary; the wrapper's
            // relative-positioning is what anchors it. Width-fit lets
            // the button hug its label without filling the row.
            width: 'fit-content', zIndex: 5, pointerEvents: 'auto',
          }}
        >
          <button
            type="button"
            onClick={handleJumpToLatest}
            data-testid="jump-to-latest"
            className="px-3 py-1.5 text-xs font-medium rounded-full shadow-md"
            style={{
              background: 'var(--primary)', color: 'white',
              border: 'none', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              fontFamily: 'var(--font-body)',
            }}
            aria-label="Jump to latest message"
          >
            ↓ New messages
          </button>
        </div>
      )}
    </div>
  );
}
